import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from "../lib/utils";
import { BlockLink, MediaSlotView } from "../render/context";
import { Section } from "./_section";
/**
 * LogoWallBlock — data-driven wall of partner / customer logos.
 *
 * Net-new, serializable contract (see @diality/contracts LogoWallData).
 * Rendered identically by the CMS canvas and the public site; only links +
 * image resolution differ, via BlockRenderProvider.
 */
const colMap = {
    2: "grid-cols-2",
    3: "grid-cols-2 @min-[640px]:grid-cols-3",
    4: "grid-cols-2 @min-[640px]:grid-cols-4",
    5: "grid-cols-2 @min-[640px]:grid-cols-3 @min-[1024px]:grid-cols-5",
    6: "grid-cols-2 @min-[640px]:grid-cols-3 @min-[1024px]:grid-cols-6",
};
const CONTAINER = "mx-auto max-w-7xl px-4 @min-[640px]:px-6 @min-[1024px]:px-8";
export function LogoWallBlock({ data }) {
    const { heading, logos } = data;
    const columns = data.columns ?? 4;
    // No logos on the public site: say so, rather than rendering an empty ~250px
    // band or nothing at all. A curated list with nothing in it has to read as
    // working-as-intended instead of broken, so the visitor gets the same
    // explicit message PersonGridBlock and NewsList give. (The CMS canvas never
    // reaches this — withPlaceholders gives an empty repeat group one ghost logo
    // item, so authors still see the slot.)
    if (logos.length === 0) {
        return (_jsx(Section, { children: _jsxs("div", { className: CONTAINER, children: [heading && (_jsx("p", { className: "mb-8 text-center text-sm font-bold uppercase tracking-wider text-muted-foreground", children: heading })), _jsx("p", { className: "text-center text-muted-foreground", children: "No items found." })] }) }));
    }
    return (_jsx(Section, { children: _jsxs("div", { className: CONTAINER, children: [heading && (_jsx("p", { className: "mb-8 text-center text-sm font-bold uppercase tracking-wider text-muted-foreground", children: heading })), _jsx("div", { className: cn("grid items-center gap-x-8 gap-y-10", colMap[columns]), children: logos.map((logo, i) => {
                        const inner = (_jsxs(_Fragment, { children: [_jsx(MediaSlotView, { image: logo.image, ratio: "5 / 2", label: "Logo", emptyRender: null, className: cn(
                                    // Keep MediaSlotView's `w-full`: the logo needs a definite
                                    // width to lay out in this centred column. Sized by `w-auto`
                                    // instead, its width came from its height, which `max-h-12`
                                    // was clamping — a circularity the browser resolves to zero,
                                    // so a logo with no caption beside it rendered at 0×0.
                                    // `object-contain` keeps the artwork's aspect ratio and
                                    // centres it, so the logo looks the same as it ever did.
                                    "max-h-12 object-contain", data.grayscale !== false && "opacity-70 grayscale") }), logo.caption && (_jsx("div", { className: "mt-2 text-sm text-muted-foreground", children: logo.caption }))] }));
                        return (_jsx("div", { className: "flex flex-col items-center justify-center", children: logo.href ? (_jsx(BlockLink, { target: "_blank", href: logo.href, className: "flex flex-col items-center justify-center", children: inner })) : (inner) }, i));
                    }) })] }) }));
}
export function logoWallFromBlock(block, resolve) {
    const s = block.scalars ?? {};
    const columns = ([2, 3, 4, 5, 6].includes(Number(s.columns)) ? Number(s.columns) : 4);
    const logos = (block.repeats?.logos ?? []).map((it) => ({
        image: resolve(it.media?.logo),
        alt: it.scalars?.alt || undefined,
        href: it.scalars?.href || undefined,
        caption: it.scalars?.caption || undefined,
    }));
    return {
        heading: s.heading || undefined,
        columns,
        grayscale: s.grayscale !== "false",
        logos,
    };
}
export const logoWallBlockEntry = {
    Component: LogoWallBlock,
    fromBlock: logoWallFromBlock,
};
//# sourceMappingURL=logo-wall.js.map